import { useState, useEffect } from 'react';
import { Save, Copy, Check } from 'lucide-react';
import { systemService, tenantSettingsService } from '../../../services';
import type { GeneralSettings } from '../../../services';
import { useToast } from '../../../components/common/ToastContainer';
import { usePermissions } from '../../../hooks/usePermissions';

interface GeneralSettingsTabProps {
  settings: GeneralSettings | null;
  onUpdate: (settings: GeneralSettings) => void;
  isSuperAdmin?: boolean;
  tenantId?: string;
}

const GeneralSettingsTab = ({
  settings,
  onUpdate,
  isSuperAdmin = true,
  tenantId = '',
}: GeneralSettingsTabProps) => {
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<GeneralSettings | null>(settings);
  const [orgCode, setOrgCode] = useState<string>('');
  const [copiedOrgCode, setCopiedOrgCode] = useState(false);

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  useEffect(() => {
    systemService.getOrgCode().then(setOrgCode).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!formData) return;

    try {
      setSaving(true);
      if (isSuperAdmin) {
        const result = await systemService.updateGeneral(formData);
        onUpdate(result);
      } else {
        // TenantAdmin: save overrides to tenant_settings
        await tenantSettingsService.updateCategory(tenantId, 'general', {
          systemName: formData.systemName,
          timeZone: formData.timeZone,
          dateFormat: formData.dateFormat,
          timeFormat: formData.timeFormat,
          language: formData.language,
        });
        onUpdate(formData);
      }
      toast.success('Settings Updated', 'General settings updated successfully');
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!formData) {
    return <div className="text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Platform & Branding — SuperAdmin only */}
      {isSuperAdmin && <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform & Branding</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FormField
              label="Platform URL"
              value={formData.platformUrl || ''}
              onChange={(e) => setFormData({ ...formData, platformUrl: e.target.value })}
            />
            <p className="text-xs text-gray-400 mt-1">
              Used in invitation emails and alerts for login/dashboard links.
            </p>
          </div>
          <FormField
            label="Company Name"
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
          />
          <div>
            <FormField
              label="Support Email"
              value={formData.supportEmail || ''}
              onChange={(e) => setFormData({ ...formData, supportEmail: e.target.value })}
            />
            <p className="text-xs text-gray-400 mt-1">
              Shown in invitation emails and system notifications. Default: support@securelink.net
            </p>
          </div>
        </div>

        <h3 className="text-sm font-medium text-gray-700 mt-5 mb-3">VPN App Download Links</h3>
        <p className="text-xs text-gray-400 mb-3">Used in VPN user invitation emails. Leave blank to omit from emails.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="iOS (App Store)"
            value={formData.appDownloadIos || ''}
            onChange={(e) => setFormData({ ...formData, appDownloadIos: e.target.value })}
          />
          <FormField
            label="Android (Google Play)"
            value={formData.appDownloadAndroid || ''}
            onChange={(e) => setFormData({ ...formData, appDownloadAndroid: e.target.value })}
          />
          <FormField
            label="macOS"
            value={formData.appDownloadMacos || ''}
            onChange={(e) => setFormData({ ...formData, appDownloadMacos: e.target.value })}
          />
          <FormField
            label="Windows"
            value={formData.appDownloadWindows || ''}
            onChange={(e) => setFormData({ ...formData, appDownloadWindows: e.target.value })}
          />
        </div>
      </div>}

      {/* Organisation Code — visible to all admins */}
      {orgCode && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-indigo-900 mb-2">VPN App Setup</h2>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-indigo-700">Organisation Code:</span>
            <span className="font-mono text-lg font-bold text-indigo-900 bg-white px-3 py-1 rounded border border-indigo-200">{orgCode}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(orgCode);
                setCopiedOrgCode(true);
                setTimeout(() => setCopiedOrgCode(false), 2000);
              }}
              className="p-1.5 hover:bg-indigo-100 rounded transition-colors"
              title="Copy to clipboard"
            >
              {copiedOrgCode
                ? <Check className="w-4 h-4 text-green-600" />
                : <Copy className="w-4 h-4 text-indigo-400" />
              }
            </button>
          </div>
          <p className="text-xs text-indigo-600 mt-2">
            Users enter this code in the VPN app during setup to connect to this organisation.
          </p>
        </div>
      )}

      {/* General Settings */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Localization & Display</h2>
        <div className="space-y-4">
          <FormField
            label="System Name"
            value={formData.systemName}
            onChange={(e) => setFormData({ ...formData, systemName: e.target.value })}
          />
          <FormField
            label="Time Zone"
            value={formData.timeZone}
            onChange={(e) => setFormData({ ...formData, timeZone: e.target.value })}
            type="select"
            options={[
              'UTC',
              'Africa/Johannesburg',
              'America/Anchorage',
              'America/Argentina/Buenos_Aires',
              'America/Chicago',
              'America/Denver',
              'America/Los_Angeles',
              'America/Mexico_City',
              'America/New_York',
              'America/Phoenix',
              'America/Sao_Paulo',
              'Asia/Bangkok',
              'Asia/Dubai',
              'Asia/Hong_Kong',
              'Asia/Jakarta',
              'Asia/Kolkata',
              'Asia/Seoul',
              'Asia/Singapore',
              'Asia/Tokyo',
              'Australia/Brisbane',
              'Australia/Melbourne',
              'Australia/Perth',
              'Australia/Sydney',
              'Europe/Berlin',
              'Europe/London',
              'Europe/Madrid',
              'Europe/Paris',
              'Europe/Rome',
              'Europe/Zurich',
              'Pacific/Auckland',
              'Pacific/Honolulu'
            ]}
          />
          <FormField
            label="Date Format"
            value={formData.dateFormat}
            onChange={(e) => setFormData({ ...formData, dateFormat: e.target.value })}
            type="select"
            options={['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'MMM D, YYYY']}
          />
          <FormField
            label="Time Format"
            value={formData.timeFormat}
            onChange={(e) => setFormData({ ...formData, timeFormat: e.target.value as '12h' | '24h' })}
            type="select"
            options={['12h', '24h']}
          />
          <FormField
            label="Language"
            value={formData.language}
            onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            type="select"
            options={['English', 'Spanish', 'French', 'German']}
          />
        </div>
      </div>

      {/* Save Button */}
      {canUpdate && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

const FormField = ({
  label,
  value,
  onChange,
  type = 'text',
  options
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  type?: 'text' | 'number' | 'select';
  options?: string[];
}) => {
  if (type === 'select' && options) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <select value={value} onChange={onChange} className="input-base w-full">
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="input-base w-full"
      />
    </div>
  );
};

export default GeneralSettingsTab;
